import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useSelector } from 'react-redux';

var Const = require('Const');

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 30, fontSize: 13 }),
  menu: (base) => ({ ...base, fontSize: 13 }),
};

const getNumColor = (value, isPending = false) => {
  if (isPending) return '#8f8f8f';
  const n = parseFloat(value);
  if (isNaN(n)) return '#0035ff';
  if (n < 4) return '#dc3545';
  if (n < 7) return '#edbd2d';
  return '#28a745';
};

const isValidNumGrade = (value) => {
  if (value === '' || value === null || value === undefined) return false;
  const n = parseFloat(value);
  return !isNaN(n) && n >= 0 && n <= 10;
};

const CalifTableCell = ({
  col,
  evalData,
  persona,
  courseEvals,
  courseSubjectID,
  token,
  createEval,
  updateEvals,
  onOpenComments,
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [pendingValue, setPendingValue] = useState(null);
  const prevSaving = useRef(false);

  const isSaving = useSelector((state) => state.view.loadingModules.savingEvaluation);

  const isNumType = col.ev && col.ev.evaluation_type === 'NUM';
  const isBlocked = col.blocked === 'Y';
  const isOpen = col.ev && col.ev.blocked !== 'Y';
  const canEdit = !isBlocked && isOpen;
  const evalExists = evalData && (evalData.qualif_value_id || evalData.qualif_value_nbr != null && evalData.qualif_value_nbr !== '');
  const hasComment = evalData && evalData.comments && String(evalData.comments).trim();
  const courseSubjectId = courseSubjectID || (courseEvals.subject && courseEvals.subject.course_subject_id);

  const pendingMatchesEval = (pending, data) => {
    if (!pending || !data) return false;
    if (isNumType) {
      return Math.abs(parseFloat(data.qualif_value_nbr) - parseFloat(pending.value)) < 0.0001;
    }
    return data.qualif_value_id === pending.value;
  };

  useEffect(() => {
    if (pendingValue && evalData && pendingMatchesEval(pendingValue, evalData)) {
      setPendingValue(null);
    }
  }, [evalData, pendingValue]);

  useEffect(() => {
    if (prevSaving.current && !isSaving && pendingValue) {
      setPendingValue(null);
    }
    prevSaving.current = isSaving;
  }, [isSaving, pendingValue]);

  const getOptions = () => {
    if (courseEvals && courseEvals.qualification_values) {
      return courseEvals.qualification_values.map((qualif) => ({
        value: qualif.qualif_value_id,
        label: qualif.short_name || qualif.name,
      }));
    }
    return [];
  };

  const getDisplayValue = () => {
    if (evalData.qualif_value_id) {
      const qualif = courseEvals.qualification_values.find(
        (p) => p.qualif_value_id === evalData.qualif_value_id,
      );
      return qualif ? qualif.short_name : '';
    }
    return evalData.qualif_value_nbr;
  };

  const getCurrentOption = () => {
    return Const.getObjectByValue(getOptions(), evalData.qualif_value_id);
  };

  const saveNumGrade = (rawValue) => {
    const trimmed = String(rawValue).trim();
    if (!isValidNumGrade(trimmed)) {
      alert('La nota debe ser un número entre 0 y 10');
      return false;
    }

    const numStr = String(parseFloat(trimmed));
    if (evalExists && Math.abs(parseFloat(evalData.qualif_value_nbr) - parseFloat(numStr)) < 0.0001) {
      setEditing(false);
      return true;
    }

    setPendingValue({ label: numStr, value: numStr });
    setEditing(false);

    if (!evalExists) {
      createEval(token, courseSubjectId, persona.persona_id, col.field, '', numStr, '');
    } else {
      const subjectId = evalData.course_subject_id || courseSubjectId;
      updateEvals(token, subjectId, persona.persona_id, col.field, { qualif_value_nbr: numStr }, {});
    }
    return true;
  };

  const startNumEdit = () => {
    setEditValue(evalExists ? String(evalData.qualif_value_nbr) : '');
    setEditing(true);
  };

  const handleNumCommit = () => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      setEditing(false);
      return;
    }
    saveNumGrade(trimmed);
  };

  const handleSelect = (sel) => {
    if (!sel) {
      setEditing(false);
      return;
    }

    setPendingValue({ label: sel.label, value: sel.value });
    setEditing(false);

    if (!evalExists) {
      const qualifValue = courseEvals.qualification_values.find(
        (q) => q.qualif_value_id === sel.value,
      );
      createEval(token, courseSubjectId, persona.persona_id, col.field, qualifValue, '', '');
    } else {
      const subjectId = evalData.course_subject_id || courseSubjectId;
      const qualifValue = courseEvals.qualification_values.find(
        (q) => q.qualif_value_id === sel.value,
      );
      updateEvals(
        token,
        subjectId,
        persona.persona_id,
        col.field,
        { qualif_value_id: sel.value },
        qualifValue,
      );
    }
  };

  const renderCommentIcon = (readOnly = false) => (
    <span
      style={{
        cursor: 'pointer',
        color: readOnly || pendingValue ? '#8f8f8f' : '#0035ff',
        marginLeft: 8,
      }}
      title="Comentario"
      onClick={(e) => {
        e.stopPropagation();
        onOpenComments(readOnly);
      }}
    >
      <i className={`fa ${hasComment ? 'fa-comment' : 'fa-comment-o'}`} aria-hidden="true"></i>
    </span>
  );

  const renderNumInput = () => (
    <input
      type="number"
      min="0"
      max="10"
      step="0.01"
      autoFocus
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleNumCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleNumCommit();
        }
        if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
      style={{
        width: 64,
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 'bold',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '2px 4px',
      }}
    />
  );

  if (!canEdit) {
    if (!evalExists) {
      return <td align="center"></td>;
    }
    const readOnlyColor = isNumType ? getNumColor(getDisplayValue()) : '#8f8f8f';
    return (
      <td align="center">
        <b style={{ color: readOnlyColor }}>{getDisplayValue()}</b>
        {hasComment && renderCommentIcon(true)}
      </td>
    );
  }

  if (editing && canEdit) {
    if (isNumType) {
      return (
        <td align="center" style={{ minWidth: 80 }}>
          {renderNumInput()}
        </td>
      );
    }
    return (
      <td align="center" style={{ minWidth: 100 }}>
        <Select
          autoFocus
          menuIsOpen
          classNamePrefix="react-select"
          styles={selectStyles}
          options={getOptions()}
          defaultValue={evalExists ? getCurrentOption() : null}
          onChange={handleSelect}
          onBlur={() => setTimeout(() => setEditing(false), 150)}
          placeholder="..."
        />
      </td>
    );
  }

  if (!evalExists && !pendingValue) {
    return (
      <td
        align="center"
        style={{ cursor: canEdit ? 'pointer' : 'default', color: '#0035ff' }}
        onClick={() => canEdit && (isNumType ? startNumEdit() : setEditing(true))}
      >
        <b>+</b>
      </td>
    );
  }

  const displayLabel = pendingValue ? pendingValue.label : getDisplayValue();
  const valueColor = isNumType
    ? getNumColor(pendingValue ? pendingValue.value : displayLabel, !!pendingValue)
    : pendingValue
      ? '#8f8f8f'
      : '#0035ff';
  const showCommentIcon = canEdit && (evalExists || pendingValue);

  return (
    <td align="center">
      <span
        style={{ cursor: canEdit && !pendingValue ? 'pointer' : 'default', color: valueColor }}
        onClick={() => {
          if (canEdit && !pendingValue) {
            isNumType ? startNumEdit() : setEditing(true);
          }
        }}
      >
        <b>{displayLabel}</b>
      </span>
      {showCommentIcon && renderCommentIcon(false)}
    </td>
  );
};

export { CalifTableCell };
